import * as t from 'io-ts';

import {hasProperty} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {makeApiType} from './apiTypes';
import {parseUploadedAttachment, RUploadedAttachment, serializeAttachment} from './attachmentModel';

/*
 * Runtime type.
 */

const RSignatureTemplateDirty = t.readonly(
  t.type({
    namespace: t.string,
    /**
     * For a shared signature, `allChannels` of `true` denotes that it's available for all team channels.
     * For a private signature, `allChannels` of `true` denotes that it's available for all private channels.
     */
    allChannels: t.boolean,
    /**
     * For a shared signature, `allPrivateChannels` of `true` denotes that it's available for all private channels for all teammates.
     * For a private signature, `allPrivateChannels` is expected to be `false` but the value is irrelevant.
     */
    allPrivateChannels: t.boolean,
    channelIds: t.readonlyArray(t.Integer),
    name: t.string,
    senderInfo: t.union([t.undefined, t.string]),
    body: t.string,
    attachments: t.union([t.readonlyArray(RUploadedAttachment), t.undefined]),
  }),
  'signatureTemplate',
);

const RSignatureDirty = t.intersection([
  RSignatureTemplateDirty,
  t.readonly(
    t.type({
      id: t.Integer,
      blurb: t.string,
      isRaw: t.boolean,
      sortOrder: t.Integer,
      updatedAt: t.Integer,
    }),
    'signature',
  ),
]);

const RSignatureErrorDirty = t.readonly(
  t.type({
    message: t.union([t.undefined, t.string]),
  }),
);

/*
 * Type.
 */

export interface SignatureTemplateModel extends t.TypeOf<typeof RSignatureTemplateDirty> {}
export interface SignatureModel extends t.TypeOf<typeof RSignatureDirty> {}
export interface SignatureErrorModel extends t.TypeOf<typeof RSignatureErrorDirty> {}
export const RSignature = t.clean<SignatureModel>(RSignatureDirty as any);
export const RSignatures = t.readonlyArray(RSignature);

/*
 * IO methods.
 */

export function parseSignature(src: any): SignatureModel {
  return {
    id: src.id,
    namespace: src.namespace,
    allChannels: src.all_channels,
    allPrivateChannels: src.all_private_channels,
    channelIds: src.channel_ids,
    name: src.name || '',
    senderInfo: src.sender_info || undefined,
    blurb: src.blurb,
    body: src.body,
    isRaw: src.is_raw,
    sortOrder: src.sort_order,
    updatedAt: src.updated_at,
    attachments: src.attachments && src.attachments.map(parseUploadedAttachment),
  };
}

function isSignatureModel(signature: SignatureModel | SignatureTemplateModel): signature is SignatureModel {
  return hasProperty(signature, 'id');
}

export function serializeSignature(src: SignatureModel | SignatureTemplateModel) {
  const signatureApiBaseBody = {
    namespace: src.namespace,
    sender_info: src.senderInfo,
    name: src.name,
    body: src.body,
    all_channels: src.allChannels,
    all_private_channels: src.allPrivateChannels,
    channel_ids: src.channelIds,
    attachments: src.attachments?.map((a) => serializeAttachment(a)),
  };

  if (isSignatureModel(src)) {
    return {
      ...signatureApiBaseBody,
      id: src.id,
      blurb: src.blurb,
      is_raw: src.isRaw,
      sort_order: src.sortOrder,
      updated_at: src.updatedAt,
    };
  }

  return signatureApiBaseBody;
}

export function parseSignatures(src: any): ReadonlyArray<SignatureModel> {
  return src.signatures && src.signatures.map(parseSignature);
}

export function parseSignatureError(src: any): SignatureErrorModel {
  const err = src.body || src;
  // Ignore transition errors.
  if (!err || err.message === 'transition prevented') {
    return {
      message: undefined,
    };
  }

  const message = err.message || err.data?.message || err.statusText || 'An error occurred';

  return {
    message,
  };
}

/*
 * API type.
 */

export const ApiSignature = makeApiType('signature', RSignature, parseSignature);
export const ApiSignatures = makeApiType('signatures', RSignatures, parseSignatures);
